/**
 * IVASSComplianceCheckboxes — click-wrap dichiarazioni pre-submit.
 *
 * FIX 6 (v1.54.0.0) — doppia checkbox obbligatoria conforme:
 *   1) Veridicità informazioni (art. 1892 c.c.)
 *   2) Presa visione MUP (Reg. IVASS 40/2018)
 *
 * Uso (controlled component):
 *   <IVASSComplianceCheckboxes
 *     signedDeclaration={x} onSignedDeclarationChange={setX}
 *     mupSeen={y}           onMupSeenChange={setY}
 *     mupFilename={f}       onMupFilenameChange={setF}
 *     testIdPrefix="fine-arts" />
 *
 * Espone anche `mupSeenMeta` costruito on-the-fly usato dal wizard per
 * popolare `signature_meta.mup_visione` nel payload di submit.
 */
import { useCallback, useEffect, useState } from "react";
import { FileText, ExternalLink } from "lucide-react";
import { useAgencyConfig } from "@/hooks/useAgencyConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MUP_VERSION = "v1.0";

export default function IVASSComplianceCheckboxes({
  signedDeclaration,
  onSignedDeclarationChange,
  mupSeen,
  onMupSeenChange,
  mupFilename,
  onMupFilenameChange,
  testIdPrefix = "wizard",
  intermediarioCodice = null,
}) {
  const { data: cfg } = useAgencyConfig();
  const mupDocs = (cfg?.documenti_ivass?.mup || [])
    .filter((d) => d.tipo === "allegato_3");

  // Se il wizard indica l'intermediario responsabile, mostra solo il suo MUP
  const filtered = intermediarioCodice
    ? mupDocs.filter((d) => d.intermediario === intermediarioCodice.replace("_colucci", ""))
    : mupDocs;
  const shown = filtered.length ? filtered : mupDocs;

  // Default filename: il primo MUP mostrato (Christian by default se presente)
  useEffect(() => {
    if (!mupFilename && shown.length) {
      const preferred = shown.find((d) => d.intermediario === "christian") || shown[0];
      onMupFilenameChange?.(preferred.filename);
    }
  }, [mupFilename, shown, onMupFilenameChange]);

  const openMup = useCallback((filename) => {
    onMupFilenameChange?.(filename);
    // Best-effort: apri PDF in nuova tab; consideriamo visione presunta all'apertura
    try {
      window.open(`${API}/public/documenti/mup/${filename}`, "_blank", "noopener");
    } catch (_) { /* ignore */ }
  }, [onMupFilenameChange]);

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-ivass-compliance-block`}>
      {/* Checkbox 1 — Veridicità */}
      <label className="flex items-start gap-3 cursor-pointer group">
        <input type="checkbox" checked={!!signedDeclaration}
                onChange={(e) => onSignedDeclarationChange(e.target.checked)}
                data-testid={`${testIdPrefix}-signature-checkbox`}
                className="mt-1 w-5 h-5 accent-[#163321] cursor-pointer shrink-0" />
        <span className="text-sm text-[#0F1F14] leading-relaxed">
          <strong>Dichiarazione di veridicità (art. 1892 c.c.):</strong> confermo
          che i dati inseriti sono veritieri, accurati e completi. Sono consapevole
          che dichiarazioni inesatte o reticenti sui rischi possono comportare la
          nullità o l&apos;annullabilità del contratto assicurativo.
        </span>
      </label>
      {!signedDeclaration && (
        <p className="text-xs text-red-700 ml-8"
            data-testid={`${testIdPrefix}-signature-warning`}>
          Accetta la dichiarazione per poter inviare la proposta.
        </p>
      )}

      {/* Checkbox 2 — MUP visione (FIX 6) */}
      <div className="pt-4 border-t border-[#E5E2DB]/60">
        <label className="flex items-start gap-3 cursor-pointer group">
          <input type="checkbox" checked={!!mupSeen}
                  onChange={(e) => onMupSeenChange(e.target.checked)}
                  data-testid={`${testIdPrefix}-mup-checkbox`}
                  className="mt-1 w-5 h-5 accent-[#163321] cursor-pointer shrink-0" />
          <span className="text-sm text-[#0F1F14] leading-relaxed">
            <strong>Presa visione del Modulo Unico Precontrattuale (MUP):</strong>{" "}
            Ho ricevuto e letto i documenti informativi precontrattuali
            (Allegato 3 e Allegato 4) prima della sottoscrizione, come previsto
            dal <em>Reg. IVASS 40/2018</em>.
          </span>
        </label>
        {shown.length > 0 && (
          <div className="mt-2 ml-8 flex flex-col gap-1"
                data-testid={`${testIdPrefix}-mup-links`}>
            {shown.map((d) => (
              <button key={d.filename} type="button"
                      onClick={() => openMup(d.filename)}
                      data-testid={`${testIdPrefix}-mup-link-${d.intermediario}`}
                      className="inline-flex items-center gap-1.5 text-xs text-[#163321] hover:underline w-fit">
                <FileText className="w-3.5 h-3.5" />
                {d.descrizione}
                <ExternalLink className="w-3 h-3 opacity-60" />
              </button>
            ))}
            {shown.length > 1 && (
              <p className="text-[10px] text-[#4A5E51]/70 mt-1">
                Consulta il MUP dell&apos;intermediario di riferimento.
              </p>
            )}
          </div>
        )}
        {!mupSeen && (
          <p className="mt-2 text-xs text-red-700 ml-8"
              data-testid={`${testIdPrefix}-mup-warning`}>
            Devi confermare la presa visione del MUP per procedere.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Costruisce il payload `mup_visione` per il submit del wizard.
 * Da inserire in `signature_meta.mup_visione` lato backend.
 */
export function buildMupVisioneMeta({ mupSeen, mupFilename }) {
  if (!mupSeen) return null;
  return {
    mup_visione_consenso: true,
    mup_visione_timestamp: new Date().toISOString(),
    mup_visione_filename: mupFilename || null,
    mup_visione_version: MUP_VERSION,
  };
}

/** Predicato: entrambe le checkbox spuntate. */
export function isIvassComplianceOk({ signedDeclaration, mupSeen }) {
  return !!signedDeclaration && !!mupSeen;
}
